import { useCallback, useEffect, useState } from 'react';
import {
  getBaselines,
  getConcerns,
  getLatest,
  getMeals,
  getMetrics,
  getSleep,
  getNutritionSummary,
  getSettings,
  getWorkouts,
  getWorkSessions,
  type Baseline,
  type Concern,
  type LatestMap,
  type MetricPoint,
  type NutritionTargets,
  type SleepSession,
  type StoredMeal,
  type Workout,
} from '../api.js';
import { lastNDays, todayBounds } from '../lib/range.js';
import { sumPoints } from '../lib/format.js';
import { localYmd, parseTargets } from '../lib/foodPage.js';

export interface TodayData {
  latest: LatestMap;
  restingSpark: MetricPoint[];
  weightSpark: MetricPoint[];
  sleep: SleepSession | null;
  caffeineToday: number;
  waterToday: number;
  activeCaloriesToday: number;
  basalCaloriesToday: number;
  caloriesInToday: number;
  proteinInToday: number;
  meals: StoredMeal[];
  workouts: Workout[];
  caffeinePoints: MetricPoint[];
  waterPoints: MetricPoint[];
  workSessions: Array<{ date: string; hours: number }>;
  targets: NutritionTargets;
  openConcerns: Concern[];
  baselines: Record<string, Baseline | null>;
}

export interface TodayState {
  data: TodayData | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Loads everything the Today page shows in one pass, and exposes `reload` so a
 * successful quick-log can optimistically refresh the affected tiles. The
 * per-action refetch scoping lives in `lib/quicklog.ts`; here we simply re-run
 * the whole load, which is cheap and always consistent.
 */
export function useToday(): TodayState {
  const [data, setData] = useState<TodayData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    let alive = true;
    setLoading(true);
    setError(null);

    const today = todayBounds();
    const week = lastNDays(7);
    const sleepWindow = lastNDays(3);
    const todayDate = localYmd(new Date());

    Promise.all([
      getLatest(['resting_hr', 'weight_kg']),
      getMetrics({ type: 'resting_hr', ...week, bucket: 'day', agg: 'avg' }),
      getMetrics({ type: 'weight_kg', ...week, bucket: 'day', agg: 'avg' }),
      getSleep(sleepWindow.from, sleepWindow.to),
      getMetrics({ type: 'caffeine_mg', ...today, bucket: 'raw' }),
      getMetrics({ type: 'water_ml', ...today, bucket: 'raw' }),
      getMetrics({ type: 'active_calories', ...today, bucket: 'raw', agg: 'sum' }),
      getMetrics({ type: 'basal_calories', ...today, bucket: 'raw', agg: 'sum' }),
      getNutritionSummary(undefined, -new Date().getTimezoneOffset()),
      getMeals(today.from, today.to),
      getWorkouts(today.from, today.to),
      getWorkSessions(todayDate, todayDate),
      getSettings(),
      getConcerns('open'),
      getBaselines(['resting_hr', 'hrv_sdnn', 'weight_kg']),
    ])
      .then(
        ([
          latest,
          resting,
          weight,
          sleep,
          caffeine,
          water,
          active,
          basal,
          nutrition,
          meals,
          workouts,
          sessions,
          settings,
          openConcerns,
          baselines,
        ]) => {
          if (!alive) return;
          // Collapse work sessions to one { date, hours } per day for the timeline.
          const byDay = new Map<string, number>();
          for (const s of sessions) byDay.set(s.date, (byDay.get(s.date) ?? 0) + (s.hours ?? 0));
          const workSessions = [...byDay.entries()].map(([date, hours]) => ({
            date,
            hours: Math.round(hours * 100) / 100,
          }));
          setData({
            latest,
            restingSpark: resting.points,
            weightSpark: weight.points,
            sleep: sleep[0] ?? null,
            caffeineToday: sumPoints(caffeine.points),
            waterToday: sumPoints(water.points),
            activeCaloriesToday: sumPoints(active.points),
            basalCaloriesToday: sumPoints(basal.points),
            caloriesInToday: nutrition.calories,
            proteinInToday: nutrition.protein_g,
            meals,
            workouts,
            caffeinePoints: caffeine.points,
            waterPoints: water.points,
            workSessions,
            targets: parseTargets(settings.nutrition_targets),
            openConcerns,
            baselines,
          });
        },
      )
      .catch((err: unknown) => {
        if (!alive) return;
        setError(err instanceof Error ? err.message : 'Failed to load your day.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => reload(), [reload]);

  return { data, loading, error, reload };
}
